import {Component, Input, OnInit, QueryList, ViewChildren, ViewEncapsulation} from '@angular/core';
import {StatisticsTypes} from '../../../../statistics/enums/statistics-types.enum';
import {ReportFilterType} from '../../../../statistics/enums/report-filter-type.enum';
import {OverTimeGraphType} from '../../../../statistics/enums/over-time-graph-type.enum';
import {DateTimeUnitTypes} from '../../../../statistics/enums/date-time-unit-types.enum';
import {DateType} from '../../../../statistics/enums/date-type.enum';
import {ReportIdentifier} from '../../../../statistics/enums/report-identifier.enum';
import {ChartParentType} from '../../../../statistics/enums/chart-parent-type.enum';
import {ChartType} from '../../../../utils/highchart/chart-type';
import {HighchartComponent} from '../../../../utils/highchart/highchart.component';
import {StatisticsApiService} from '../../../../services/api/statistics/statistics-api.service';
import {StatisticsTypesList} from '../../../../statistics/enums/statistics.constants';
import {DataCacheApiService} from '../../../services/map/data-cache-api/data-cache-api.service';
import {takeWhile} from 'rxjs/operators';
import {MetaDataService} from '../../../../services/metaData/meta-data.service';
import * as _ from 'lodash';
import {AcDialogService} from 'ac-infra';
import {ChartDialogComponent} from '../../../../dialogs/chart-dialog/chart-dialog.component';

@Component({
  selector: 'peer-connection-summary',
  templateUrl: './peer-connection-summary.component.html',
  styleUrls: ['./peer-connection-summary.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class PeerConnectionSummaryComponent implements OnInit {

  @Input()
  set selection(selection: any) {
    this.dataInitialization(selection);
  }

  constructor(private statisticsApi: StatisticsApiService,
              private metaDataService: MetaDataService,
              private dataCacheApiService: DataCacheApiService,
              private acDialogService: AcDialogService,
              ) {
  }

  routingAttemptsStatistics: any;
  unsuccessfulRoutesStatistics: any;
  callsStatistics: any;
  chartParentType: any;
  chartType: any;
  chartIsStack: any;
  peerConnectionStatistics: any;
  peerConnection: any;

  optionsForRoutingAttempts: any;
  optionsForUnsuccessfulRoutes: any;
  optionsForCalls: any;
  isQualityEnabled: boolean;
  dataReady = false;

  topologyGroups = '';

  routingAttemptsChart: HighchartComponent;
  unsuccessfulRoutesChart: HighchartComponent;
  callsChart: HighchartComponent;


  @ViewChildren(HighchartComponent)
  charts: QueryList<HighchartComponent>;

  ngOnInit(): void {
    this.dataCacheApiService.getDataReady().pipe(takeWhile(() => this.dataReady === false)).
    subscribe(x => {
      if (x === true) {
        this.dataReady = true;
        this.initializeQualityBasedRoutingConfigurations();
      }
    });

    /*this.sharedEventService.on(this, 'onlineDiagramSelectionChanged', (event, shapes, connections) => {
      this.peerConnectionStatistics = [];
      if (shapes.length === 0 && connections.length === 1 && connections[0].connectionType === 'peerConnection') {
        this.peerConnection = connections[0].dataItem;
        this.getChartsData();
        this.initializeData();
      }
    });*/

    this.routingAttemptsStatistics = this.getRoutingAttemptsStatisticsNames();
    this.unsuccessfulRoutesStatistics = this.getUnsuccessfulRoutesStatisticsNames();
    this.callsStatistics = this.getCallsStatisticsNames();

    this.chartParentType = ChartParentType.LINE;
    this.chartType = ChartType[ChartParentType.LINE].type;
    this.chartIsStack = ChartType[ChartParentType.LINE].stack;

    this.optionsForRoutingAttempts = {
      parentType: this.chartParentType,
      type: this.chartType,
      stacked: this.chartIsStack,
      series: [],
      title: {
        text: 'Routing attempts per 5 minutes',
        fontSize: '12px'
      },
      subTitle: '',
      startDate: undefined,
      interval: 0,
      animation: true,
      exporting: false
    };

    this.optionsForUnsuccessfulRoutes = {
      parentType: this.chartParentType,
      type: this.chartType,
      stacked: this.chartIsStack,
      series: [],
      title: {
        text: 'Unsuccessful Routes Per 5 Minutes',
        fontSize: '12px'
      },
      subTitle: '',
      startDate: undefined,
      interval: 0,
      animation: true,
      exporting: false
    };

    this.optionsForCalls = {
      parentType: this.chartParentType,
      type: this.chartType,
      stacked: this.chartIsStack,
      series: [],
      title: {
        text: 'Calls Per 5 Minutes',
        fontSize: '12px'
      },
      subTitle: '',
      startDate: undefined,
      interval: 0,
      animation: true,
      exporting: false
    };
  }

  initializeCharts(): void {
    const chartArray = this.charts.toArray();
    this.routingAttemptsChart = chartArray[0];
    this.unsuccessfulRoutesChart = chartArray[1];
    this.callsChart = chartArray[2];
  }

  openRoutingAttemptsChartDialog(): void {
    this.acDialogService.open(ChartDialogComponent, {
      dialogData: this.optionsForRoutingAttempts
    });
  }

  openUnsuccessfulRoutesChartDialog(): void {
    this.acDialogService.open(ChartDialogComponent, {
      dialogData: this.optionsForUnsuccessfulRoutes
    });
  }

  openCallsChartDialog(): void {
    this.acDialogService.open(ChartDialogComponent, {
      dialogData: this.optionsForCalls
    });
  }

  getChartsData(): void {
    if (this.peerConnection === undefined || this.peerConnection === null) {
      return;
    }

    const parameters = {
      reportIdentifier: ReportIdentifier.peerConnectionOverTime,
      dateType: DateType.RELATIVE,
      relativeTimeUnit: DateTimeUnitTypes.HOUR,
      relativeTimeUnitValue: 3,
      overTimeGraphType: OverTimeGraphType.MULTIPLE_STATISTICS,
      availableFilters: [
        ReportFilterType.DATE,
        ReportFilterType.MULTIPLE_STATISTICS,
        ReportFilterType.ELEMENTS,
        ReportFilterType.OVER_TIME_GRAPH_TYPE
      ],
      multipleStatistics: [
        StatisticsTypes.ROUTING_ATTEMPTS,
        StatisticsTypes.UNSUCCESSFUL_ROUTES,
        StatisticsTypes.ALTERNATIVE_ATTEMPTS,
        StatisticsTypes.DESTINATION_NOT_ROUTABLE,
        StatisticsTypes.DESTINATION_CALL
      ],
      elements: [this.peerConnection.id]
    };

    this.statisticsApi.getReportForChart(parameters)
      .then((value) => {
        this.initializeCharts();
        this.setRoutingAttemptsReport(value);
        this.setUnsuccessfulRoutesReport(value);
        this.setCallsReport(value);
      }).catch(() => {
    });
  }

  setRoutingAttemptsReport(value): void {
    const series = value.series.filter((element) => {
      return this.routingAttemptsStatistics.indexOf(element.name) !== -1;
    });

    this.optionsForRoutingAttempts.series = series;
    this.optionsForRoutingAttempts.startDate = value.startDate;
    this.optionsForRoutingAttempts.interval = value.interval;

    this.routingAttemptsChart.setOptions(this.optionsForRoutingAttempts);
  }

  setUnsuccessfulRoutesReport(value): void {
    const series = value.series.filter((element) => {
      return this.unsuccessfulRoutesStatistics.indexOf(element.name) !== -1;
    });

    this.optionsForUnsuccessfulRoutes.series = series;
    this.optionsForUnsuccessfulRoutes.startDate = value.startDate;
    this.optionsForUnsuccessfulRoutes.interval = value.interval;

    this.unsuccessfulRoutesChart.setOptions(this.optionsForUnsuccessfulRoutes);
  }

  setCallsReport(value): void {
    const series = value.series.filter((element) => {
      return this.callsStatistics.indexOf(element.name) !== -1;
    });

    this.optionsForCalls.series = series;
    this.optionsForCalls.startDate = value.startDate;
    this.optionsForCalls.interval = value.interval;

    this.callsChart.setOptions(this.optionsForCalls);
  }

  getRoutingAttemptsStatisticsNames(): any {
    return StatisticsTypesList.filter((element) => {
      return element.value === StatisticsTypes.ROUTING_ATTEMPTS;
    }).map((element) => {
      return element.name;
    });
  }

  getUnsuccessfulRoutesStatisticsNames(): any {
    return StatisticsTypesList.filter((element) => {
      return element.value === StatisticsTypes.UNSUCCESSFUL_ROUTES ||
        element.value === StatisticsTypes.ALTERNATIVE_ATTEMPTS ||
        element.value === StatisticsTypes.DESTINATION_NOT_ROUTABLE;
    }).map((element) => {
      return element.name;
    });
  }

  getCallsStatisticsNames(): any {
    return StatisticsTypesList.filter((element) => {
      return element.value === StatisticsTypes.DESTINATION_CALL;
    }).map((element) => {
      return element.name;
    });
  }

  initializeQualityBasedRoutingConfigurations(): void {
    this.isQualityEnabled = this.dataCacheApiService.getQualityBasedRouting().isQualityEnabled;
  }

  generateAdministrativeStateLabel(): string {
    return this.metaDataService.PeerConnectionAdminStatus[this.peerConnection.adminState].label;
  }

  initializeData(): void {
    this.topologyGroups = _.isEmpty(this.peerConnection.topologyGroups) ? '' :
        this.peerConnection.topologyGroups.map(group => group.name).join(', ');
  }

  private dataInitialization(selection: any): void {
    if (_.isNil(selection)) { return; }

    const shapes = selection.nodes;
    const connections = selection.edges;
    this.peerConnectionStatistics = [];
    if (shapes.length === 0 && connections.length === 1 && connections[0].connectionType === 'peerConnection') {
      this.peerConnection = connections[0].dataItem;
      this.getChartsData();
      this.initializeData();
    }
  }
}
